import React, { useState, useEffect, useCallback } from "react";
import { PlusOutlined, MinusOutlined, DeleteOutlined } from "@ant-design/icons";
import { TablePlug, NumberSelector } from "./Styled";
import { apiGetCarts, apiPutCarts, apiGetActivityContent } from "api/api";
import { convertTimeStamp } from "utility/functions/convertTimestamp";
import { useSelector, useDispatch } from "react-redux";
import { updateCartsAmount } from "store/slice/index";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  MAX_TICKETS_PER_ITEM,
  getMaxTicketAmount,
} from "utility/options/cart";

const ChartTable = ({ setCartsAmount }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const open = useSelector((state) => state.charts.isChartOpen);

  const isLogin =
    sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;

  const normal = "票券類型：一般票種 Ticket Type: General Admission";
  const pr = "票券類型：免費票種 Ticket Type: Free Admission";

  // 取得購物車資料
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let details = [];
      if (isLogin) {
        const res = await apiGetCarts();
        details = res.data.content.details;
      } else {
        details = JSON.parse(localStorage.getItem("carts")) || [];
      }
      setTableData(details);
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsLoading(false);
    }
  }, [isLogin]);

  useEffect(() => {
    fetchData();
  }, [open, fetchData]);

  useEffect(() => {
    dispatch(updateCartsAmount(tableData.length));
  }, [tableData, dispatch]);

  useEffect(() => {
    setCartsAmount(tableData.length);
  }, [tableData, setCartsAmount]);

  // 更新購物車資料
  const updatedData = async (data = tableData) => {
    setIsLoading(true);
    try {
      if (isLogin) {
        await apiPutCarts(data);
        fetchData();
      } else {
        localStorage.setItem("carts", JSON.stringify(data));
        setTableData(data);
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: "產品 Item",
      dataIndex: "name",
      key: "name",
      render: (text, record, index) => {
        return (
          <>
            <p className="mb-2">{record.groupTitle}</p>
            <p className="mb-2">{record.title}</p>
            <p className="text-SKF-G-002 text-14">{`${convertTimeStamp(
              record.startDateTime,
              "MM/DD ddd - HH:mm"
            )}（opens at ${convertTimeStamp(
              record.admissionStartDateTime,
              "HH:mm"
            )}）`}</p>
            <p>NT${record?.price}</p>
            <p className="text-SKF-G-002 text-14">
              {record?.type === 1 ? normal : pr}
            </p>
            <NumberSelector
              controls={false}
              min={1}
              max={MAX_TICKETS_PER_ITEM}
              value={record?.amount}
              readOnly={true}
              className="mt-5 w-1/2 md:w-1/6"
              addonBefore={
                <MinusOutlined
                  onClick={() => calculate(record, "minus")}
                  className="text-white "
                />
              }
              addonAfter={
                <PlusOutlined
                  onClick={() => calculate(record, "plus")}
                  className="text-white "
                />
              }
            />
            <DeleteOutlined
              className="mt-6 pl-2 text-xl"
              style={{
                lineHeight: "24px",
                height: "24px",
              }}
              onClick={() => handleDelete(record)}
            />
          </>
        );
      },
    },
    {
      title: "總計 Subtotal",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (text, record) => {
        return <p className="m-0">NT${record?.amount * record?.price}</p>;
      },
    },
  ];

  const handleDelete = (record) => {
    const filterData = tableData.filter(
      (item) => item.sourceId !== record.sourceId
    );
    updatedData(filterData);
  };

  const calculate = async (record, type) => {
    console.log("record", record, type);

    let maxQuantity = MAX_TICKETS_PER_ITEM; // 無法取得實際庫存時只套用單筆上限

    try {
      if (record.activityId) {
        const res = await apiGetActivityContent({ id: record.activityId });
        const targetTicket = res.data.content.tickets.find(
          (ticket) => ticket.id === record.sourceId
        );

        if (targetTicket) {
          maxQuantity = getMaxTicketAmount(targetTicket.remainQuantity);
        }
      }
    } catch (error) {
      console.error(
        "Error fetching activity content, skipping stock check",
        error
      );
    }

    const newTableData = tableData.map((item) => {
      if (item?.sourceId === record?.sourceId) {
        let newAmount = item.amount;
        if (type === "plus") {
          if (item.amount + 1 > maxQuantity) {
            message.error(t("cart.limitReached", { max: maxQuantity }));
            // 已超過上限時拉回上限，但至少保留 1 張（刪除請用垃圾桶）
            newAmount = Math.max(1, Math.min(item.amount, maxQuantity));
          } else {
            newAmount = item.amount + 1;
          }
        } else if (type === "minus") {
          newAmount = item.amount > 1 ? item.amount - 1 : 1;
        }
        return { ...item, amount: newAmount };
      }
      return item;
    });

    setTableData(newTableData);
    updatedData(newTableData); // 傳入最新資料
  };

  return (
    <TablePlug
      bordered={false}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      loading={isLoading}
      className="border-none bg-SKF-G-004 [&_.ant-table-tbody>tr.ant-table-placeholder]:bg-transparent [&_.ant-empty-description]:text-white"
      locale={{
        emptyText: (
          <div className="space-y-1 py-6 text-center text-white">
            <p className="m-0 font-medium">購物車目前是空的</p>
            <p className="m-0 text-sm">Your cart is empty</p>
            <p className="m-0 text-sm text-white/70">
              快去挑選喜歡的場次，把它加進購物車吧！
            </p>
          </div>
        ),
      }}
      summary={(pageData) => {
        let ttl = 0;
        pageData.forEach(({ price, amount }) => {
          ttl += price * amount;
        });
        return (
          <>
            <TablePlug.Summary.Row className="bg-SKF-G-004 text-white">
              <TablePlug.Summary.Cell index={0}>
                <h4>總計</h4>
              </TablePlug.Summary.Cell>
              <TablePlug.Summary.Cell index={1} colSpan={2}>
                <h4>NT${ttl}</h4>
              </TablePlug.Summary.Cell>
            </TablePlug.Summary.Row>
          </>
        );
      }}
    ></TablePlug>
  );
};

export default ChartTable;
